// Route prefetching — when a nav element enters the viewport, kick off the
// matched route's lazy chunk import so the next click resolves instantly.
//
// Used via the `v-prefetch="<RouteLocationRaw>"` directive registered from
// `main.ts` with the app's router. The directive observes its element with a
// single shared IntersectionObserver and unobserves immediately after the
// first prefetch — chunks ship once, then live in the browser's module map.
//
// Failure modes are silent on purpose. Prefetch is a hint, not a contract; a
// missing IO, a network drop, or a rejected dynamic import must never affect
// the user-visible navigation.
import type { App, Directive } from 'vue';
import type { RouteLocationRaw, Router } from 'vue-router';

type LazyComponent = () => unknown;

const prefetchedFactories = new WeakSet<LazyComponent>();
let intersectionObserver: IntersectionObserver | null = null;
const observed = new Map<Element, { router: Router; to: RouteLocationRaw }>();

function ensureObserver(): IntersectionObserver | null {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        return null;
    }
    if (intersectionObserver == null) {
        intersectionObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }
                    const meta = observed.get(entry.target);
                    if (meta != null) {
                        prefetchRoute(meta.router, meta.to);
                        intersectionObserver?.unobserve(entry.target);
                        observed.delete(entry.target);
                    }
                }
            },
            // 200px root margin so the chunk is in flight before the link
            // actually scrolls into view. Cheap on bandwidth, big win on
            // perceived latency.
            { rootMargin: '200px' },
        );
    }
    return intersectionObserver;
}

export function prefetchRoute(router: Router, to: RouteLocationRaw): void {
    try {
        const resolved = router.resolve(to);
        for (const record of resolved.matched) {
            const components = (record.components ?? {}) as Record<string, unknown>;
            for (const key in components) {
                const component = components[key];
                if (typeof component !== 'function') {
                    continue;
                }
                const factory = component as LazyComponent;
                if (prefetchedFactories.has(factory)) {
                    continue;
                }
                prefetchedFactories.add(factory);
                const result = factory();
                if (result != null && typeof (result as Promise<unknown>).then === 'function') {
                    // Swallow rejection — a failed prefetch is a no-op, the
                    // real navigation will surface the chunk-load error.
                    (result as Promise<unknown>).catch(() => undefined);
                }
            }
        }
    } catch {
        // Bad `to`? Router not yet ready? Skip silently — prefetch is best-effort.
    }
}

function scheduleIdle(fn: () => void): void {
    if (typeof window === 'undefined') {
        fn();
        return;
    }
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: 2_000 });
    } else {
        setTimeout(fn, 0);
    }
}

export function createPrefetchDirective(
    router: Router,
): Directive<HTMLElement, RouteLocationRaw | undefined> {
    return {
        mounted(el, binding) {
            const to = binding.value;
            if (to == null) {
                return;
            }
            const io = ensureObserver();
            if (io == null) {
                // No IntersectionObserver — schedule a one-off idle prefetch
                // so we still get some benefit on legacy browsers.
                scheduleIdle(() => prefetchRoute(router, to));
                return;
            }
            observed.set(el, { router, to });
            io.observe(el);
        },
        beforeUnmount(el) {
            if (intersectionObserver != null && observed.has(el)) {
                intersectionObserver.unobserve(el);
                observed.delete(el);
            }
        },
    };
}

export function installPrefetchDirective(app: App, router: Router): void {
    app.directive('prefetch', createPrefetchDirective(router));
}

export const __testing__ = {
    reset(): void {
        intersectionObserver?.disconnect();
        intersectionObserver = null;
        observed.clear();
    },
    getObserved(): ReadonlyMap<Element, { router: Router; to: RouteLocationRaw }> {
        return observed;
    },
    hasPrefetched(factory: LazyComponent): boolean {
        return prefetchedFactories.has(factory);
    },
};
